import { hash } from 'ohash';

/**
 * Groups API accommodation booking parts into a product info for accommodations
 *
 * 1. Filter out only accommodation booking parts
 * 2. Group them by hotel, paassengers and dates
 */
export function transformProductInfosAccommodation(bookingParts: ApiBookingPart[], response: ApiSchema['BookingResponse']): ProductInfoAccommodation[] {
    const apiBooking = response?.booking;
    if (!apiBooking) {
        return [];
    }

    return bookingParts
        .filter(bp => bp.objectType === 'Accommodation')
        .filter(bp => bp.status !== 'REFUNDED')
        .reduce<ProductInfoAccommodation[]>((list, bookingPart) => {
            assignAccommodation(list, bookingPart, apiBooking);
            return list;
        }, []);
}

/**
 * Assigns an accommodation booking part to the appropriate product info.
 *
 * Booking parts that belong together are grouped together. Based on:
 * - Accommodation details (hotel)
 * - Passengers
 * - Dates
 *
 * As a result you get one accommodation product info per hotel/passenger/date combination, which is how we want to display it in the app.
 *
 * Example: 4 People go to a hotel with 2 rooms in June, 3 people (1 room) go into a hotel in september
 * - 2 booking parts --> grouped into 1 product info (june)
 * - 1 booking part --> grouped into another product info (september)
 */
function assignAccommodation(
    list: ProductInfo[],
    accommodation: ApiSchema['Accommodation'],
    apiBooking: NonNullable<ApiSchema['BookingResponse']['booking']>,
) {
    const type = 'Accommodation';
    const dateFrom = zurichDate(accommodation.validFrom);
    const dateUntil = zurichDate(accommodation.validUntil);
    const accDetails = accommodation?.accommodationDetails;
    const accommodationDetails = {
        id: accDetails?.id ?? '',
        name: accDetails?.name,
        address: {
            street: accDetails?.address?.street ?? '',
            countryCode: accDetails?.address?.countryCode ?? '',
            postCode: accDetails?.address?.postCode ?? '',
            city: accDetails?.address?.city ?? '',
        },
        phoneNumber: accDetails?.phoneNumber,
        email: accDetails?.email,
        location: {
            latitude: Number.parseFloat(accDetails?.location?.latitude ?? ''),
            longitude: Number.parseFloat(accDetails?.location?.longitude ?? ''),
        },
    } satisfies AccommodationDetails;

    const passengers = apiBooking.passengers
        .filter(p => accommodation.passengerIds.includes(p.id))
        .map(p => transformPassenger(p));

    // Currently no listings for accommodations available
    let listingId = accommodation.listingId;
    if (!listingId) {
        listingId = '';
    }
    const isLocalListing = false;

    // Group items by this identifier
    const id = hash([
        accommodationDetails.id,
        type,
        dateFrom.getTime(),
        dateUntil?.getTime(),
    ]);

    // Find or create (first) product info item
    let productInfo = list.find(p => p.id === id);
    if (!productInfo) {
        productInfo = {
            id,
            type,
            listingId,
            isLocalListing,
            dateFrom,
            dateUntil,
            accommodationDetails,
            passengers,
        } satisfies ProductInfoAccommodation;
        list.push(productInfo);
    }
}
